import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "../../hooks/useRouter";
import { useDispatch } from "react-redux";
import "../Chat/Chat.css";

// components
import GMTChatDashboard from "../../components/GMTChatDashboard/GMTChatDashboard";
import ChatPromptContainer from "../../components/ChatPromptContainer/ChatPromptContainer";
import MessageBox from "../../components/MessageBox/MessageBox";
import ImageModal from "../../components/ImageModal/ImageModal";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

// store
import {
    generateCustomGMTSessionId,
    fetchGMTChatCompletions,
    fetchGMTUserConversation,
    updateGMTFeedbackRequest
} from "../../store/apiActions";

// utility & constants

import { useEventEmitter } from "../../contexts/EventEmmiter";
import { useConstants } from "../../constant/constantProvider";
import { generateCustomId, mapConversationMessages, scrollToLatestViewPoint } from "../../utils/commonHelper";
import { handleUnauthorized } from "../../utils/apiHelper";
import { downloadFileFromBase64 } from "../../utils/fileHelper";
import textToSpeech from "../../utils/textToSpeech";
import { sessionManager } from "../../utils/utility";
import { sanitizeTextInput, sanitizeTextPreservingHTML } from "../../utils/textUtils";
import { useAuth } from "../../hooks/useAuth";
import useIsMobile from "../../hooks/useIsMobile";


import GMTBotIcon from "../../assets/images/gmt-logo.svg";
import downArrow from "../../assets/new-images/dropdown-icon.svg";

// Removed math block preservation since MathJax is no longer used

function GMTChat() {
    const { subscribe, emit } = useEventEmitter();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    const { 
        FEEDBACK_SUBMIT_SUCCESS, 
        FILE_DOWNLOAD_SUCCESS,
        SESSION_EXPIRED 
    } = useConstants();

    const [isGMTDashboardEnable, setIsGMTDashboardEnable] = useState(true);
    const [message, setMessage] = useState("");
    const [suggestionAPICall, setSuggestionAPICall] = useState(false);
    const [cardClicked, setCardClicked] = useState(false);
    const chatEndRef = useRef(null);
    const [converstation, setConverstation] = useState([]);
    const [uiConversation, setUIConversation] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState({});
    const [feedbackInput, setFeedbackInput] = useState("");
    const feedbackInputRef = useRef(null);
    const feedbackContainerRef = useRef(null);

    // eslint-disable-next-line
    const [isTextBoxDisabled, setIsTextBoxDisabled] = useState(false);

    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [sessionId, setSessionId] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editedText, setEditedText] = useState("");
    const richInputRef = useRef(null);
    const streamReaderRef = useRef(null);
    const streamAbortControllerRef = useRef(null);

    const [citationsData, setCitationsData] = useState({});
    const [discussionPointsData, setDiscussionPointsData] = useState([]);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [feedbackButtonsDisabled, setFeedbackButtonsDisabled] = useState(() => {
        const savedDisabled = localStorage.getItem("feedbackButtonsDisabled");
        return savedDisabled && savedDisabled !== "undefined"
            ? JSON.parse(savedDisabled)
            : {};
    });
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState({ url: "", filename: "" });

    const { username, fullName: fullname, ugdnId: userUGDN } = useAuth();

    useEffect(() => {
        const unsubscribe = subscribe("newGMTChatDirect", () => {
            handleNewSessionContinue();
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = subscribe("clearChat", () => {
            clearChatData();
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const sessionId = sessionManager.getSessionId("GMTSessionId");
        const userId = username;
        fetchUserConversation(userId, sessionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearChatData = () => {
        setConverstation([]);
        setUIConversation([]);
        setSelectedFeedback({});
        setFeedbackButtonsDisabled({});
        localStorage.removeItem("logout");
        setIsChatLoading(false);
        setCitationsData({});
        sessionManager.setSessionId(null, "GMTSessionId");

        // Clear message and RichInputBox when starting new chat
        setMessage("");
        if (richInputRef.current) {
            richInputRef.current.clear();
        }
    };

    const generateGMTSessionId = async () => {
        try {
            const payload = {
                user_id: username,
                ugdn_id: userUGDN || "",
            }
            const response = await dispatch(generateCustomGMTSessionId(payload));
            if (response?.status === 401) {
                handleUnauthorized(navigate, undefined, undefined, SESSION_EXPIRED);
                return;
            }
            if (response?.session_id) {
                return response.session_id;
            }
            return null;
        } catch (error) {
            console.error("Error generating GMT Session ID:", error);
            return null;
        }
    }

    const handleSendMessage = async ({
        isEdit = false,
        editedConvo = [],
        editedMessage = null,
        messageID = null,
        selectedModel = null,
        query = null,
    } = {}) => {
        const userMessageId = generateCustomId();

        const contentToSend = message?.trim()
            ? message
            : query ? query : "";

        const userMessage = isEdit
            ? {
                role: "user",
                content: editedMessage?.content || contentToSend,
                id: messageID,
            }
            : {
                role: "user",
                content: contentToSend,
                id: userMessageId,
            };

        const placeholderBotMessage = {
            role: "assistant",
            content: "Loading...",
            isPlaceholder: true,
            id: isEdit ? messageID : userMessageId,
            feedback: "",
        };

        const updatedUI = isEdit
            ? [...editedConvo, userMessage, placeholderBotMessage]
            : [...converstation, userMessage, placeholderBotMessage];
        setUIConversation(updatedUI);

        scrollToLatestViewPoint(chatEndRef, isMobile, false);

        if (!isEdit) {
            setMessage("");
            // Clear the RichInputBox
            if (richInputRef.current) {
                richInputRef.current.clear();
            }
        }

        setIsLoading(true);

        try {
            let GMTSessionId = sessionManager.getSessionId("GMTSessionId");

            // Check validity
            if (!GMTSessionId || GMTSessionId === "undefined") {
                GMTSessionId = await generateGMTSessionId();
            }

            if (!GMTSessionId) {
                console.error("Unable to initialize session. Please try again.");
            }

            setSessionId(GMTSessionId);
            sessionManager.setSessionId(GMTSessionId, "GMTSessionId");

            const messagesForAPI = isEdit
                ? [...editedConvo, userMessage]
                : [...converstation, userMessage];

            const payload = {
                user_id: username,
                ugdn_id: userUGDN || "",
                session_id: GMTSessionId || sessionId,
                query: isEdit ? userMessage.content : contentToSend,
                stream: false,
            };
            await handleSendMessageAPI(payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI);
        } catch (error) {
            console.error("Error in handleSendMessage:", error);
            handleAbortMessageError(isEdit ? editedConvo : converstation);
            toast.error(error.message || "Failed to send message");
        }
    };

    const handleAbortMessageError = (messagesForAPI) => {
        const errorMessage = {
            role: "assistant",
            content: "Sorry, I'm having trouble right now",
            feedback: '',
        };
        const updatedConvo = [...messagesForAPI, errorMessage];
        setConverstation(updatedConvo);
        setUIConversation(updatedConvo);
        streamReaderRef.current = null;
        setIsLoading(false);
    };

    const handleSendMessageAPI = async (payload, userMessageId, placeholderBotMessage, messageID, isEdit, messagesForAPI) => {
        try {
            const abortController = new AbortController();
            streamAbortControllerRef.current = abortController;
            const response = await dispatch(fetchGMTChatCompletions(payload));

            // Check if response is an error object (axios error structure)
            // Error objects have response.response.status, successful responses have response.response as a string
            const errorStatus = response?.response?.status;

            if (errorStatus === 400) {
                toast.error("Session expired. Please login again.");
                localStorage.clear();
                setTimeout(() => {
                    navigate("/sign-in");
                }, 1500);
                return;
            }

            if (errorStatus === 422) {
                const errorMessage = response?.response?.data?.message || response?.message || "Validation error occurred. Please try again.";
                toast.error(errorMessage);
                handleAbortMessageError(messagesForAPI);
                return;
            }

            // Check if response is an error object (has response.response.status indicating axios error)
            if (errorStatus) {
                const errorMessage = response?.response?.data?.message || response?.message || "An error occurred. Please try again.";
                toast.error(errorMessage);
                handleAbortMessageError(messagesForAPI);
                return;
            }

            // Extract assistant response from previous_conversation
            let assistantContent = "";

            // If no assistant content found, use the response field as fallback
            // Ensure response.response is a string before assigning (only for successful responses)
            if (!assistantContent && response?.response && typeof response.response === 'string') {
                assistantContent = response.response;
            }

            // Use message_id from API response if available, otherwise fall back to client-generated ID
            const messageId = response?.message_id || (isEdit ? messageID : userMessageId);

            // Map citations and page_base64 if present in response
            if (response?.citations && response.citations.length > 0) {
                setCitationsData((prev) => ({
                    ...prev,
                    [messageId]: {
                        citations: response.citations,
                        page_base64: response.page_base64 || []
                    },
                }));
            }

            if (response?.discussion_points && response.discussion_points.length > 0) {
                setDiscussionPointsData((prev) => ({
                    ...prev,
                    [messageId]: response.discussion_points,
                }));
            }

            // Create the final bot message
            const finalBotMessage = {
                role: "assistant",
                content: assistantContent || "I apologize, but I couldn't generate a response. Please try again.",
                id: messageId,
                feedback: '',
            };

            if (response?.session_title || response?.thread_title) {
                emit("updateThreads");
            }

            // Update conversations
            const finalConvo = [...messagesForAPI, finalBotMessage];
            setConverstation(finalConvo);
            setUIConversation(finalConvo);
        } catch (error) {
            console.error("GMT Assistant error:", error);
            handleAbortMessageError(messagesForAPI);
        } finally {
            setIsLoading(false);
        }
    };

    const stopStreaming = () => {
        if (streamAbortControllerRef.current) {
            try {
                streamAbortControllerRef.current.abort();
            } catch (err) {
                console.warn("AbortController error:", err);
            }
        }
        setIsLoading(false);
    };

    const fetchUserConversation = async (user_id, session_id) => {
        setIsChatLoading(true);
        setConverstation([]);
        setUIConversation([]);
        setIsGMTDashboardEnable(false);
        if (!user_id || !session_id) {
            handleNewSessionContinue();
            return;
        }
        try {
            const params = {
                session_id: session_id,
            };
            const response = await dispatch(fetchGMTUserConversation(params));
            let conversionData = response;

            const mappedMessages = mapConversationMessages(conversionData?.messages || [], {
                useRoleSuffixId: false,
                includeGraph: false,
            });

            // Map citations and page_base64 from API response
            const mappedCitations = {};
            (conversionData?.messages || []).forEach((message) => {
                if (message.message_id && message.citations && message.citations.length > 0) {
                    mappedCitations[message.message_id] = {
                        citations: message.citations,
                        page_base64: message.page_base64 || []
                    };
                }
            });

            const mappedDiscussionPoints = {};
            (conversionData?.messages || []).forEach((message) => {
                if (message.message_id && message.discussion_points && message.discussion_points.length > 0) {
                    mappedDiscussionPoints[message.message_id] = message.discussion_points;
                }
            });

            setDiscussionPointsData(mappedDiscussionPoints);

            setCitationsData(mappedCitations);
            setUIConversation(mappedMessages);
            setConverstation(mappedMessages);

            setIsChatLoading(false);
            scrollToLatestViewPoint(chatEndRef, isMobile, true);
        } catch (error) {
            setIsChatLoading(false);
            console.error("Error fetching user conversation:", error);
        }
    };

    useEffect(() => {
        if (suggestionAPICall === true) {
            handleSendMessage();
            setSuggestionAPICall(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestionAPICall]);

    useEffect(() => {
        // Load selectedFeedback
        const savedSelectedFeedback = JSON.parse(
            localStorage.getItem("selectedFeedback")
        );
        if (savedSelectedFeedback) {
            setSelectedFeedback(savedSelectedFeedback);
        }
    }, []);

    const handleInputChange = (text) => {
        setMessage(text);
    };

    const handleEditClick = (index, role, id) => {
        if (role === "user") {
            setEditingId(index);
            setEditedText(uiConversation[index].content);
        }
    };

    const handleSave = async (editIndex, editData) => {
        const updatedFeedback = {};
        // Keep only entries with keys < editIndex
        for (const key in selectedFeedback) {
            if (parseInt(key) < editIndex) {
                updatedFeedback[key] = selectedFeedback[key];
            }
        }
        // Update state
        setSelectedFeedback(updatedFeedback);
        // Trim feedbackButtonsDisabled
        const trimmedButtonsDisabled = {};
        for (const key in feedbackButtonsDisabled) {
            if (parseInt(key) < editIndex) {
                trimmedButtonsDisabled[key] = feedbackButtonsDisabled[key];
            }
        }
        setFeedbackButtonsDisabled(trimmedButtonsDisabled);
        // Optional: Persist back to localStorage if needed
        localStorage.setItem(
            "feedbackButtonsDisabled",
            JSON.stringify(trimmedButtonsDisabled)
        );
        setEditingId(null);
        if (!editedText.trim()) return;
        const editingIndex = converstation.findIndex(
            (msg, index) => index === editIndex && msg.role === "user"
        );
        if (editingIndex === -1) {
            return;
        }
        stopStreaming();

        // Clear citations for the message ID being edited
        if (editData?.id) {
            setCitationsData((prev) => {
                const updated = { ...prev };
                delete updated[editData.id];
                return updated;
            });
        }

        const updatedUserMessage = {
            role: "user",
            content: sanitizeTextPreservingHTML(editedText),
        };
        const trimmedConvo = converstation.slice(0, editingIndex);
        await handleSendMessage({
            isEdit: true,
            editedConvo: trimmedConvo,
            editedMessage: updatedUserMessage,
            messageID: editData?.id,
        });

        setEditedText("");
    };


    useEffect(() => {
        const unsubscribe = subscribe("thread", async (agentType, sessionId) => {
            if (agentType !== "GMT") return;

            if (!sessionId) return;

            setSessionId(sessionId)
            const localSessionId = sessionManager.getSessionId('GMTSessionId');
            setEditingId(null);
            if (localSessionId !== sessionId) {
                fetchUserConversation(username, sessionId);
                stopStreaming();
            }
        });

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribe]);


    useEffect(() => {
        if (cardClicked) {
            handleSendMessage();
            setCardClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardClicked]);

    const [showScrollButton, setShowScrollButton] = useState(false);
    const chatContainerRef = useRef(null);

    const handleScroll = () => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShowScrollButton(!isAtBottom);
        }
    };

    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, []);

    useEffect(() => {
        handleScroll();
    }, [uiConversation]);




    const handleNewSessionContinue = async () => {
        if (location?.pathname !== '/gmt-chat') {
            navigate('/gmt-chat');
        }

        if (!sessionManager.getSessionId("GMTSessionId")) {
            setIsGMTDashboardEnable(true);
        }

        stopStreaming();
        await emit("resetSelectedThread", true);
        clearChatData();
        setIsLoading(false);
        setIsChatLoading(false);
        sessionManager.setSessionId(null, "GMTSessionId");
    };

    const handleFeedback = async (index, type, data, comment = "") => {
        // Add this line after you setSelectedFeedback(...)
        setUIConversation((prev) =>
            prev.map((msg, idx) =>
                idx === index ? { ...msg, feedback: type } : msg
            )
        );

        setConverstation((prev) =>
            prev.map((msg, idx) =>
                idx === index ? { ...msg, feedback: type } : msg
            )
        );

        const oppositeType = type === "like" ? "dislike" : "like";
        // Reset input if type changed
        if (selectedFeedback[index] !== type) {
            setFeedbackInput((prev) => {
                const updated = { ...prev };
                if (updated[index]) delete updated[index];
                return updated;
            });
            setFeedbackInput("");
        }

        // Update feedback data and selection

        setSelectedFeedback((prev) => ({
            ...prev,
            [index]: type,
        }));

        // Disable only the selected type and reset the opposite type
        setFeedbackButtonsDisabled((prev) => ({
            ...prev,
            [index]: {
                [type]: true,
                [oppositeType]: false,
            },
        }));

        await handleFeedbackSubmit(type, index, data, comment);

        setTimeout(() => {
            if (
                feedbackInputRef.current &&
                feedbackContainerRef.current.offsetParent !== null
            ) {
                feedbackInputRef.current.focus();
                feedbackContainerRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }, 0);
    };

    const handleFeedbackSubmit = async (type, index, pair, commentParam = "") => {
        if (submittingFeedback) return;

        setSubmittingFeedback(true);

        try {
            const newSessionId = sessionManager.getSessionId("GMTSessionId") || sessionId;

            const comment = sanitizeTextInput(
                commentParam !== "" ? commentParam : (feedbackInput?.[index]?.comment?.trim() || "")
            );

            const requestBody = {
                session_id: newSessionId,
                message_id: pair?.id ?? "",
                feedback: type ?? "",
                feedback_comment: comment,
                user_id: username,
            };

            const feedbackAction = updateGMTFeedbackRequest;

            const response = await dispatch(feedbackAction(requestBody)).unwrap();
            
            if (response) {
                toast.info(FEEDBACK_SUBMIT_SUCCESS || "Feedback submitted successfully");
                setFeedbackButtonsDisabled(prev => ({ ...prev, [index]: { [type]: true } }));
            }

            setFeedbackInput((prev) => ({
                ...prev,
                [index]: { comment: "" },
            }));

        } catch (error) {
            console.error("Feedback submit error:", error);
            toast.error("Failed to submit feedback");
        } finally {
            setSubmittingFeedback(false);
        }
    };

    useEffect(() => {
        return () => {
            // Stop any ongoing speech when component unmounts
            textToSpeech.stop();
        };
    }, []);

    const handleCitationPreview = (messageId, citationIndex) => {
        const citationData = citationsData[messageId];
        if (!citationData || !citationData.page_base64 || !citationData.page_base64[citationIndex]) {
            toast.error("File data not available for preview");
            return;
        }

        const base64Data = citationData.page_base64[citationIndex];
        const imageSrc = `data:image/jpeg;base64,${base64Data}`;

        // Extract filename from citation if available, otherwise generate one
        const citation = citationData.citations?.[citationIndex];
        let filename = citation || citation?.name;

        if (filename.toLowerCase().endsWith('.pdf')) {
            filename = filename.replace(/\.pdf$/i, '.jpg');
        } else if (!filename.match(/\.(jpg|jpeg|png|gif)$/i)) {
            filename = `${filename}.jpg`;
        }

        setModalImage({ url: imageSrc, filename });
        setIsImageModalOpen(true);
    }

    // eslint-disable-next-line
    const handleCitationDownload = (messageId, citationIndex) => {
        const citationData = citationsData[messageId];
        if (!citationData || !citationData.page_base64 || !citationData.page_base64[citationIndex]) {
            toast.error("File data not available for download");
            return;
        }

        const base64Data = citationData.page_base64[citationIndex];
        const citationName = citationData.citations[citationIndex] || "File";

        try {
            downloadFileFromBase64(base64Data, citationName, "jpg");
            toast.info(FILE_DOWNLOAD_SUCCESS || "File downloaded successfully");
        } catch (error) {
            toast.error("Failed to download file");
        }
    };

    const routeToGMTChat = () => {
        setIsGMTDashboardEnable(false);
    }

    const hasChatMessages = (uiConversation?.length ?? 0) > 0;

    return (
        <>
            <div className={`upl-genie-page-wrapper ${!hasChatMessages ? 'is-empty' : 'is-active'}`}>
                {/* Main container — fills remaining space */}
                <div className="upl-genie-container">
                    <LoadingScreen isLoading={isChatLoading} />

                    {/* Chat body */}
                    <div className="upl-chat-body" ref={chatContainerRef}>
                        <div className="upl-chat-content-column">
                            {(!isChatLoading && isGMTDashboardEnable && !hasChatMessages) ? (
                                <div className="upl-new-chat-content">
                                    <GMTChatDashboard routeToGMTChat={routeToGMTChat} />
                                </div>
                            ) : !isGMTDashboardEnable && (
                                <div className="upl-messages-list">
                                    {(uiConversation || []).map((pair, index) => {
                                        const isLastMessage = index === uiConversation.length - 1;
                                        const currentBotIcon = GMTBotIcon;
                                        const citations = citationsData?.[pair?.id]?.citations || [];
                                        const discussionPoints = discussionPointsData?.[pair?.id] || [];

                                        return (
                                            <MessageBox
                                                key={index}
                                                pair={pair}
                                                index={index}
                                                editingId={editingId}
                                                editedText={editedText}
                                                setEditedText={setEditedText}
                                                isMobile={isMobile}
                                                fullname={fullname}
                                                isTextBoxDisabled={isTextBoxDisabled}
                                                showEditPrompt={false}
                                                handleEditClick={handleEditClick}
                                                handleSave={handleSave}
                                                setEditingId={setEditingId}
                                                botIcon={currentBotIcon}
                                                isLoading={isLoading}
                                                isLastMessage={isLastMessage}
                                                useMathJax={false}
                                                onFeedback={handleFeedback}
                                                feedbackState={pair.feedback}
                                                citations={citations}
                                                discussionPoints={discussionPoints}
                                                onCitationClick={(citation) => {
                                                    const idx = citations.indexOf(citation);
                                                    if (idx !== -1) handleCitationPreview(pair.id, idx);
                                                }}
                                                generatedBy={null}
                                                showFeedback={false}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    </div>

                    {/* Unified Footer */}
                    {!isGMTDashboardEnable &&
                        <div className="upl-genie-footer">
                            <div className="upl-chat-content-column">
                                {/* Scroll-to-bottom button */}
                                {hasChatMessages && showScrollButton && (
                                    <div className="upl-scroll-btn-row">
                                        <button
                                            className="upl-scroll-bottom-btn"
                                            onClick={() => scrollToLatestViewPoint(chatEndRef, isMobile, true, true)}
                                        >
                                            <img src={downArrow} alt="Scroll to bottom" />
                                        </button>
                                    </div>
                                )}

                                <ChatPromptContainer
                                    value={message}
                                    onChange={handleInputChange}
                                    isLoading={isLoading}
                                    placeholder="Ask for latest business updates"
                                    handleSendMessage={handleSendMessage}
                                    handleStopStreaming={stopStreaming}
                                    richInputRef={richInputRef}
                                />
                            </div>
                        </div>}
                </div>
            </div>
            {isImageModalOpen && (
                <ImageModal
                    image={modalImage}
                    onClose={() => setIsImageModalOpen(false)}
                    showZoomInZoomOut={false}
                    showDownload={true}
                />
            )}
        </>
    );
}

export default GMTChat;